import { useState } from "react";
import { MapPinned } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { usePlatformCan } from "../../lib/platform";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import { REPORT_FIELD, REPORT_STATUS, REPORT_STATUS_TONE, osmLink } from "../../lib/directoryLabels";

const DECISIONS = [["verifie", "Données vérifiées et exactes"], ["a_corriger", "Demander une correction à l'entreprise"], ["position_retiree", "Retirer la position GPS"], ["rejete", "Rejeter le signalement"]];
const OPEN = ["ouvert", "a_corriger", "corrige"];
const Coords = ({ lat, lng }) => lat != null ? <a className="font-mono text-blue-600 hover:underline" href={osmLink(lat, lng)} target="_blank" rel="noreferrer">{lat}, {lng}</a> : <span className="text-slate-400">non renseignée</span>;

const Decision = ({ r, onDone }) => {
  const [d, setD] = useState("verifie");
  const [note, setNote] = useState("");
  const submit = async () => {
    try { await api.post(`/admin/location-reports/${r.id}/decision`, { decision: d, note }); toast.success("Décision enregistrée"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg bg-slate-50 p-3 sm:flex-row sm:items-end">
      <select className="field-select sm:w-64" value={d} onChange={(e) => setD(e.target.value)} data-testid={`admin-report-decision-${r.id}`}>
        {DECISIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <Textarea rows={1} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Motif / note (obligatoire)" className="flex-1" data-testid={`admin-report-note-${r.id}`} />
      <Button size="sm" disabled={note.trim().length < 2} onClick={submit} data-testid={`admin-report-submit-${r.id}`}>Valider</Button>
    </div>
  );
};

const ReportCard = ({ r, canDecide, onDone }) => (
  <li className="card-surface space-y-3 p-4" data-testid={`admin-report-${r.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div><p className="font-medium">{r.agency?.name || "Agence supprimée"} <span className="text-slate-500">· {r.company?.name}</span></p>
        <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString("fr-FR")} · {r.fields.map((f) => REPORT_FIELD[f]).join(", ")}{r.email && ` · ${r.email}`}</p></div>
      <span className={`rounded-full px-2 py-0.5 text-[11px] ${REPORT_STATUS_TONE[r.status]}`} data-testid={`admin-report-status-${r.id}`}>{REPORT_STATUS[r.status]}</span>
    </div>
    <p className="text-sm text-slate-700">« {r.comment} »</p>
    <div className="grid gap-2 text-xs sm:grid-cols-3">
      <p>Au signalement : <Coords lat={r.snapshot?.latitude} lng={r.snapshot?.longitude} /></p>
      <p>Actuelle : <Coords lat={r.agency?.latitude} lng={r.agency?.longitude} /></p>
      <p>Suggérée : <Coords lat={r.suggested_latitude} lng={r.suggested_longitude} /></p>
    </div>
    {r.agency && <p className="text-xs text-slate-500">{[r.agency.address, r.agency.city, r.agency.country].filter(Boolean).join(", ") || "Adresse non renseignée"} · {[r.agency.phone, r.agency.email, r.agency.hours].filter(Boolean).join(" · ")}</p>}
    {r.company_note && <p className="text-xs text-blue-700">Entreprise : {r.company_note}</p>}
    {r.admin_note && <p className="text-xs text-indigo-700">Décision : {r.admin_note}</p>}
    {canDecide && OPEN.includes(r.status) && <Decision r={r} onDone={onDone} />}
  </li>
);

export default function AdminLocations() {
  const [status, setStatus] = useState("");
  const can = usePlatformCan();
  const { data, loading, error, reload } = useApi(`/admin/location-reports?status=${status}`);
  return (
    <div>
      <PageHeader title="Annuaire : données signalées" description="Vérifiez les positions et coordonnées d'agences signalées comme incorrectes par les visiteurs." />
      <div className="mb-4 flex flex-wrap gap-2">
        {[["", "Tous"], ...Object.entries(REPORT_STATUS)].map(([v, l]) => (
          <Button key={v} size="sm" variant={status === v ? "default" : "outline"} className="rounded-full" onClick={() => setStatus(v)} data-testid={`admin-reports-filter-${v || "all"}`}>
            {l}{v && data?.counts ? ` (${data.counts[v]})` : ""}
          </Button>
        ))}
      </div>
      {loading && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.items.length && <EmptyState icon={MapPinned} title="Aucun signalement" text="Aucune donnée signalée pour ce filtre." />}
      <ul className="space-y-3">{data?.items.map((r) => <ReportCard key={r.id} r={r} canDecide={can("moderation:decide")} onDone={reload} />)}</ul>
    </div>
  );
}
